import { Loader2, Plus, Server } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { useCreateMailProvider, useMailProviders } from '@/features/access/api'
import { toastError } from '@/features/access/ui'

const KINDS = [
  { value: 'google', labelKey: 'admin.mail.providerKinds.google' },
  { value: 'smtp_imap', labelKey: 'admin.mail.providerKinds.smtp_imap' },
  { value: 'smtp_relay', labelKey: 'admin.mail.providerKinds.smtp_relay' },
] as const

export function MailProvidersPanel() {
  const { t } = useTranslation()
  const list = useMailProviders(true)
  const create = useCreateMailProvider()
  const [name, setName] = useState('Firma Gmail')
  const [kind, setKind] = useState<string>('google')
  const [isDefault, setIsDefault] = useState(true)
  const providers = list.data ?? []
  const hasGoogle = providers.some((p) => p.kind === 'google')

  const submit = async () => {
    if (!name.trim()) return
    try {
      await create.mutateAsync({ name: name.trim(), kind, isDefault })
      toast.success(t('admin.mail.providerCreated'))
      setName(kind === 'google' ? 'Firma Gmail' : '')
    } catch (e) {
      toastError(e)
    }
  }

  return (
    <section className="overflow-hidden rounded-2xl border bg-card shadow-xs">
      <div className="flex flex-wrap items-center gap-2 border-b px-4 py-3">
        <Server className="size-4 text-muted-foreground" aria-hidden />
        <div className="min-w-0">
          <h2 className="text-sm font-semibold">{t('admin.mail.providersTitle')}</h2>
          <p className="text-xs text-muted-foreground">{t('admin.mail.providersHint')}</p>
        </div>
      </div>

      <div className="space-y-4 p-4">
        {list.isLoading ? (
          <Skeleton className="h-16 rounded-xl" />
        ) : providers.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('admin.mail.providersEmpty')}</p>
        ) : (
          <ul className="divide-y rounded-xl border">
            {providers.map((p) => (
              <li key={p.id} className="flex items-center gap-3 px-3 py-2.5">
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium">{p.name}</span>
                  <span className="block text-xs text-muted-foreground">{p.kind}</span>
                </span>
                {p.isDefault ? <Badge tone="primary">{t('admin.mail.providerDefault')}</Badge> : null}
              </li>
            ))}
          </ul>
        )}

        <div className="grid gap-3 rounded-xl border bg-muted/20 p-3 sm:grid-cols-[1fr_10rem_auto]">
          <div className="space-y-1.5">
            <Label htmlFor="mp-name">{t('admin.mail.providerName')}</Label>
            <Input id="mp-name" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>{t('admin.mail.providerKind')}</Label>
            <Select
              value={kind}
              onChange={setKind}
              options={KINDS.map((k) => ({ value: k.value, label: t(k.labelKey) }))}
            />
          </div>
          <div className="flex items-end gap-2">
            <label className="mb-2 flex items-center gap-2 text-xs text-muted-foreground">
              <input
                type="checkbox"
                className="size-4 rounded border"
                checked={isDefault}
                onChange={(e) => setIsDefault(e.target.checked)}
              />
              {t('admin.mail.providerDefault')}
            </label>
            <Button type="button" onClick={() => void submit()} disabled={create.isPending || !name.trim()}>
              {create.isPending ? <Loader2 className="animate-spin" /> : <Plus />}
              {t('admin.mail.providerAdd')}
            </Button>
          </div>
        </div>

        {!hasGoogle ? <p className="text-xs text-muted-foreground">{t('admin.mail.providersGoogleHint')}</p> : null}
      </div>
    </section>
  )
}
